import type { JSONSchema4 } from 'json-schema';
import type { PageConfig, PageType, Parser } from '@sap/ux-specification-types';
import { ObjectAggregation } from '../ObjectAggregation';
import type { PageEditAggregationData } from '../ObjectAggregation';
import { AggregationCreationForm, SCHEMA_CREATION_FORM, SortingOptions } from '../types';
import type { PageData, CreationFormOptions, PageAnnotations, AllowedMoveRange } from '../types';
import { ActionAggregation } from './ActionAggregation';

// Remove when latest '@sap/ux-specification-types' is released
export enum ActionType {
    Annotation = 'Annotation',
    Copy = 'Copy',
    Criticality = 'Criticality',
    Custom = 'Custom',
    Standard = 'Standard',
    StandardWithoutAnnotation = 'StandardWithoutAnnotation'
}

export interface ActionsContainers {
    top: AllowedMoveRange;
    bottom: AllowedMoveRange;
}

/**
 * Represents an aggregation for actions container object.
 */
export class ActionsAggregation extends ObjectAggregation {
    sortableList = true;
    childClass = ActionAggregation;
    allowedAnnotationCreationForms = [AggregationCreationForm.NativeAction, AggregationCreationForm.NativeNavigation];
    sortableCollection: string | undefined = 'actions';
    i18nKey = 'ACTIONS';

    /**
     * Creates an instance of `ActionsAggregation`.
     *
     * @param data Optional aggregation data object used to initialize properties.
     * @param schema Optional JSON schema fragment associated with this aggregation.
     */
    constructor(data?: PageEditAggregationData, schema?: JSONSchema4) {
        super(data, schema);
        // Custom creation form - check schema if supported
        if (schema?.properties && this.schema?.additionalProperties && this.schema?.metadata?.type !== 'Aggregation') {
            this.schemaCreationForms = [
                {
                    name: AggregationCreationForm.CustomAction,
                    kind: SCHEMA_CREATION_FORM,
                    title: 'PAGE_EDITOR_OUTLINE_ADD_CUSTOM_ACTIONS_TITLE',
                    disabled: false
                }
            ];
        }
    }

    /**
     * Method adds aggregation object.
     * Overwritten to mark standard action.
     *
     * @param name Name of aggregation.
     * @param aggregation Aggregation to add.
     * @param path Array of path to aggregation.
     * @param order Order index.
     * @param overwrite Overwrite existing aggregation.
     * @returns Added aggregation.
     */
    public addAggregation(
        name: string,
        aggregation: ObjectAggregation,
        path: Parser.PropertyPath,
        order?: number,
        overwrite?: boolean
    ): ObjectAggregation {
        aggregation = super.addAggregation(name, aggregation, path, order, overwrite);
        if (aggregation instanceof ActionAggregation && aggregation.name) {
            switch (aggregation.schema?.actionType) {
                case ActionType.Standard: {
                    aggregation.markAsStandardAction();
                    break;
                }
                case ActionType.Copy: {
                    aggregation.sortableItem = SortingOptions.Readonly;
                    break;
                }
            }
        }
        return aggregation;
    }

    /**
     * Overwritten method for data update of object page actions
     * Method receives current values for actions and detects custom actions.
     *
     * @param data Data which should be used for value population.
     * @param page Page config data.
     * @param pageType Page type.
     * @param path Aggregation path.
     * @param annotations Annotations data.
     */
    public updatePropertiesValues(
        data: PageData,
        page: PageConfig,
        pageType: PageType,
        path: Parser.PropertyPath,
        annotations: PageAnnotations
    ): void {
        super.updatePropertiesValues(data, page, pageType, path, annotations);
        this.formSchema = this.additionalProperties?.aggregations['actions'];
        const actions = data || {};
        for (const id in actions) {
            const action = this.aggregations[id] as ActionAggregation;
            if (action?.name && action.schema?.actionType === 'Standard') {
                action.markAsStandardAction();
            } else if (action?.schema && (action.schema.actionType === 'Custom' || !action.schema.annotationPath)) {
                action.markAsCustomAction();
            }
        }
    }

    /**
     * Method provides creation options based on its related annotation node.
     *
     * @param annotations Page annotations.
     * @returns Array of creation forms.
     */
    protected getNativeNodeCreationForms(annotations: PageAnnotations | undefined): CreationFormOptions[] {
        if (this.parent) {
            if (this.parent.name === 'footer') {
                this.allowedAnnotationCreationForms = [AggregationCreationForm.NativeAction];
            }
        }
        return super.getNativeNodeCreationForms(annotations);
    }
}
